import { Box, Card, rem, Skeleton, Table } from '@mantine/core';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PaginationFooter } from 'components/PaginationFooter';
import { i18nKeys, useTranslation } from 'locales';
import { type ReactNode, useMemo } from 'react';
import { isEmpty, times } from 'remeda';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import type { Id } from 'types';
import type { Pagination } from 'types/storeTypes';

import { buildViewColumnDefs, type ViewTableMeta } from '../buildViewColumnDefs';
import { TableSelectionBanner } from '../CustomTableGrid/TableSelectionBanner';
import type { GridColumn, TableRow } from '../CustomTableGrid/types';
import { EDIT_COLUMNS_COLUMN_ID, SELECTION_COLUMN_ID } from '../columnIds';

const AUX_COLUMN_IDS = new Set<string>([SELECTION_COLUMN_ID, EDIT_COLUMNS_COLUMN_ID]);

const ROW_MIN_HEIGHT = rem(48);
const CELL_MIN_WIDTH = rem(80);
const CELL_MAX_WIDTH = rem(400);

const auxColumnStyle = (columnId: string): React.CSSProperties | undefined =>
  AUX_COLUMN_IDS.has(columnId) ? { width: '1%', whiteSpace: 'nowrap' } : undefined;

const STEP_COLOR: Record<number, string> = {
  1: 'yellow-4',
  2: 'orange-5',
  3: 'orange-9',
  4: 'red-5',
  5: 'success-0',
};

const stepColor = (item: TableRow): string | undefined => {
  const stepCollection = (item as { step_collection?: number }).step_collection;
  return stepCollection ? STEP_COLOR[stepCollection] : undefined;
};

type Props = {
  columns: GridColumn[];
  isSelectable?: boolean;
  items: TableRow[];
  pagination: Pagination;
  sortBy?: string;
  sortOrder?: string;
  onSort: (columnName: string) => void;
  selectedIds: readonly Id[];
  areAllItemsOnThisPageSelected: boolean;
  areAllItemsAcrossPagesSelected: boolean;
  onToggleSelectAllOnPage: () => void;
  onToggleAcrossPages: () => void;
  onToggleRow: (id: Id) => void;
  isLoading?: boolean;
  isFetching: boolean;
  filtersNumber: number;
  noResultMessage?: string;
  pageLimit: number;
  onClickRow: (item: TableRow, e: React.MouseEvent<HTMLTableRowElement>) => void;
  actionsCol?: (item: TableRow) => ReactNode;
  onEditColumns: () => void;
  currentPage?: number;
  currentPageLimit?: number;
  setPage: (page: { selected: number }) => void;
  setPageLimit?: (pageLimit: number) => () => void;
};

export function ViewTableGrid({
  columns,
  isSelectable,
  items,
  sortBy,
  sortOrder,
  onSort,
  selectedIds,
  onToggleRow,
  areAllItemsOnThisPageSelected,
  areAllItemsAcrossPagesSelected,
  onToggleSelectAllOnPage,
  onToggleAcrossPages,
  actionsCol,
  isLoading,
  isFetching,
  filtersNumber,
  noResultMessage,
  pagination,
  pageLimit,
  onClickRow,
  currentPage,
  currentPageLimit,
  setPage,
  setPageLimit,
  onEditColumns,
}: Props) {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter();
  const isShowingLoading = Boolean(isLoading || isFetching);
  const isShowingEmpty = !isShowingLoading && isEmpty(items);
  const emptyMessage =
    filtersNumber > 0
      ? t(i18nKeys.NO_RESULT_FOR_THIS_SEARCH)
      : (noResultMessage ?? t(i18nKeys.NO_RESULT));

  const columnDefs = useMemo(
    () => buildViewColumnDefs({ columns, isSelectable }),
    [columns, isSelectable],
  );

  const meta = useMemo<ViewTableMeta>(
    () => ({
      t,
      formatter,
      actionsCol,
      sortBy,
      sortOrder,
      onSort,
      selectedIds,
      toggleRow: onToggleRow,
      areAllItemsOnThisPageSelected,
      onToggleSelectAllOnPage,
      onEditColumns,
    }),
    [
      t,
      formatter,
      actionsCol,
      sortBy,
      sortOrder,
      onSort,
      selectedIds,
      onToggleRow,
      areAllItemsOnThisPageSelected,
      onToggleSelectAllOnPage,
      onEditColumns,
    ],
  );

  const table = useReactTable({
    data: items,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    meta,
  });

  const ariaSortFor = (columnId: string): 'ascending' | 'descending' | undefined => {
    if (columnId !== sortBy) return undefined;
    return sortOrder === 'asc' ? 'ascending' : 'descending';
  };

  const leafColumns = table.getAllLeafColumns();

  return (
    <Card
      radius={8}
      p={0}
      withBorder
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Box style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Table stickyHeader highlightOnHover horizontalSpacing="md" verticalSpacing="xs" fz="xs">
          <Table.Thead bg="purple.1">
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Table.Th
                    key={header.id}
                    aria-sort={ariaSortFor(header.column.id)}
                    style={{ verticalAlign: 'middle', ...auxColumnStyle(header.column.id) }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody data-testid={isShowingLoading ? 'view-table-loading' : undefined}>
            <TableSelectionBanner
              areAllItemsOnThisPageSelected={areAllItemsOnThisPageSelected}
              areAllItemsAcrossPagesSelected={areAllItemsAcrossPagesSelected}
              totalObjects={pagination?.total_objects ?? 0}
              pageLimit={pageLimit}
              itemsLength={items.length}
              onToggleAcrossPages={onToggleAcrossPages}
            />
            {isShowingEmpty && (
              <Table.Tr>
                <Table.Td colSpan={leafColumns.length}>{emptyMessage}</Table.Td>
              </Table.Tr>
            )}
            {isShowingLoading
              ? times(columns.length, rowIndex => (
                  <Table.Tr key={`skeleton-${rowIndex}`}>
                    {leafColumns.map(column => (
                      <Table.Td key={column.id}>
                        <Skeleton h={rem(25)} my={rem(5)} radius="md" />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              : table.getRowModel().rows.map(row => (
                  <Table.Tr
                    key={row.id}
                    style={{ height: ROW_MIN_HEIGHT, cursor: 'pointer' }}
                    onClick={event => onClickRow(row.original, event)}
                    onAuxClick={event => {
                      // Only the middle button (open-in-new-tab); right-click must fall
                      // through to the browser context menu, not navigate the row.
                      if (event.button === 1) onClickRow(row.original, event);
                    }}
                  >
                    {row.getVisibleCells().map(cell => {
                      const isAuxCell = AUX_COLUMN_IDS.has(cell.column.id);
                      const stepStripColor =
                        cell.column.id === SELECTION_COLUMN_ID
                          ? stepColor(row.original)
                          : undefined;
                      const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                      return (
                        <Table.Td
                          key={cell.id}
                          style={{
                            ...auxColumnStyle(cell.column.id),
                            // background-image (not -color) so the hover highlight survives.
                            ...(stepStripColor
                              ? {
                                  backgroundImage: `linear-gradient(to right, var(--mantine-color-${stepStripColor}) ${rem(4)}, transparent ${rem(4)})`,
                                }
                              : {}),
                          }}
                        >
                          {isAuxCell ? (
                            content
                          ) : (
                            <Box
                              style={{
                                minWidth: CELL_MIN_WIDTH,
                                maxWidth: CELL_MAX_WIDTH,
                                overflowWrap: 'break-word',
                              }}
                            >
                              {content}
                            </Box>
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
          </Table.Tbody>
        </Table>
      </Box>
      <PaginationFooter
        total={pagination?.total_objects ?? 0}
        currentPage={currentPage ?? pagination?.current_page ?? 1}
        pageLimit={currentPageLimit ?? pageLimit}
        setCurrentPage={page => setPage({ selected: page - 1 })}
        setPageLimit={limit => setPageLimit?.(limit)?.()}
      />
    </Card>
  );
}
